// Photosynthesis lesson — full data model for v4
// - 5 concept slides + intro + recap + container slides (mcq, subjective, drag-drop, blank)
// - 5 concept-check MCQs
// - 6 subjective questions with solved-example & practice modes
// - 1 drag-drop activity (Inputs vs Outputs)
// - Real-life examples + teacher-help library
// - Sections config powering the grouped navigator

export const photosynthesisLesson = {
    sessionName: "Photosynthesis",
    title: "Photosynthesis",
    subtitle: "How plants turn sunlight into food",
    grade: "Grade 7 · Biology",
    chapter: "Chapter 3 · Plants & Life",
    subject: "Biology",
    coverImage:
        "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=85",
    coverAlt: "A green leaf reaching towards sunlight",

    /* ---- physical slides ---- */
    slides: [
        // CONCEPT SECTION ----------------------------------------------------
        {
            id: "intro",
            section: "concept",
            mode: "intro",
            title: "Photosynthesis",
            subtitle: "How plants turn sunlight into food",
            estimatedSeconds: 60,
            tags: ["overview"],
            notes: ["Welcome the class — set the energy for today's lesson."],
            misconceptions: ["Plants 'eat' soil through their roots."],
            fragments: [
                {
                    caption:
                        "Hi class! Today we'll explore how plants make their own food using just sunlight, water, and air.",
                },
                {
                    caption:
                        "By the end, you'll know what photosynthesis is, where it happens, and why life on Earth depends on it.",
                },
            ],
        },
        {
            id: "what-is",
            section: "concept",
            mode: "concept_teaching",
            title: "What is Photosynthesis?",
            estimatedSeconds: 90,
            tags: ["definition"],
            heroImage:
                "https://images.unsplash.com/photo-1540927550647-43699cb14916?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwaG90b3N5bnRoZXNpcyUyMGxlYWZ8ZW58MHx8fHwxNzc4MjMxNjU3fDA&ixlib=rb-4.1.0&q=85",
            notes: [
                "Etymology helps memory — 'photo' (light) + 'synthesis' (making).",
            ],
            misconceptions: ["Confusing 'food' with 'sunlight' — sunlight is energy, not food."],
            fragments: [
                {
                    bullet: "Plants make their own food",
                    caption:
                        "Unlike us, plants don't eat. They make their own food.",
                },
                {
                    bullet: "Photo = light, Synthesis = making",
                    caption:
                        '"Photo" means light. "Synthesis" means making. So — making with light.',
                },
            ],
        },
        {
            id: "ingredients",
            section: "concept",
            mode: "concept_teaching",
            title: "Ingredients Needed",
            estimatedSeconds: 80,
            tags: ["inputs"],
            heroImage:
                "https://images.unsplash.com/photo-1540927550647-43699cb14916?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwaG90b3N5bnRoZXNpcyUyMGxlYWZ8ZW58MHx8fHwxNzc4MjMxNjU3fDA&ixlib=rb-4.1.0&q=85",
            notes: ["Tie each ingredient to where it comes from in nature."],
            misconceptions: [],
            fragments: [
                {
                    bullet: "Carbon dioxide — from the air",
                    caption:
                        "Plants absorb carbon dioxide through tiny pores on their leaves.",
                },
                {
                    bullet: "Water — from the soil",
                    caption: "Roots pull water up from the soil into the leaf.",
                },
                {
                    bullet: "Sunlight — the energy source",
                    caption: "Sunlight provides the energy that powers the whole process.",
                },
            ],
        },
        {
            id: "sunlight",
            section: "concept",
            mode: "concept_teaching",
            title: "Role of Sunlight",
            estimatedSeconds: 80,
            tags: ["energy"],
            heroImage:
                "https://images.unsplash.com/photo-1540927550647-43699cb14916?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwaG90b3N5bnRoZXNpcyUyMGxlYWZ8ZW58MHx8fHwxNzc4MjMxNjU3fDA&ixlib=rb-4.1.0&q=85",
            notes: ["Sunlight is the energy — not the food. Repeat this often."],
            misconceptions: ["Students may say 'plants eat sunlight.' — clarify it's energy."],
            fragments: [
                {
                    bullet: "Sunlight is energy, not food",
                    caption:
                        "Sunlight is the fuel — it powers the chemical reaction.",
                },
                {
                    bullet: "Plants kept in the dark cannot make food",
                    caption:
                        "Without sunlight, photosynthesis stops — and the plant slowly starves.",
                },
            ],
        },
        {
            id: "watch-leaf",
            section: "concept",
            mode: "video_teaching",
            title: "Watch: Inside a leaf",
            estimatedSeconds: 90,
            tags: ["video"],
            videoSrc:
                "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
            notes: [
                "Pause the video at the green parts and ask 'why is the leaf green?'",
                "After playback, transition smoothly to the chloroplast diagram.",
            ],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "Let's watch a short clip — notice the green structures inside the leaf.",
                },
            ],
        },
        {
            id: "chlorophyll",
            section: "concept",
            mode: "diagram_teaching",
            title: "Role of Chlorophyll",
            estimatedSeconds: 90,
            tags: ["organelle"],
            diagramImage:
                "https://static.prod-images.emergentagent.com/jobs/c60cd966-3b4a-4c48-a979-cfff21045932/images/05610b168983c1ab4b9ce645c478b8e2c5fc5f1c851c163eb47ece4ea6c533dd.png",
            notes: [
                "Use the pen to circle the chloroplast as you explain.",
            ],
            misconceptions: ["Chloroplast vs chlorophyll — organelle vs pigment."],
            fragments: [
                {
                    label: "Chloroplast — tiny green factory",
                    caption:
                        "Inside leaf cells are tiny factories called chloroplasts.",
                },
                {
                    label: "Chlorophyll — captures sunlight",
                    caption:
                        "Chloroplasts contain chlorophyll — the green pigment that absorbs sunlight.",
                },
            ],
        },
        {
            id: "equation",
            section: "concept",
            mode: "concept_teaching",
            title: "Photosynthesis Equation",
            estimatedSeconds: 90,
            tags: ["equation"],
            heroImage:
                "https://static.prod-images.emergentagent.com/jobs/c60cd966-3b4a-4c48-a979-cfff21045932/images/64e033821d5f9af9f91f51148cff02c7921622eb5ba416269952c9b14ccaff73.png",
            notes: ["Walk through inputs → outputs side of the equation."],
            misconceptions: [],
            fragments: [
                {
                    bullet: "Inputs: water + carbon dioxide + sunlight",
                    caption: "Plants take in water, CO₂ and capture sunlight.",
                },
                {
                    bullet: "Outputs: glucose + oxygen",
                    caption: "They produce glucose — their food — and release oxygen.",
                },
                {
                    bullet: "6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂",
                    caption: "And here's the chemical equation.",
                },
            ],
        },

        // CONTAINER SLIDES ----------------------------------------------------
        // Driven by state: activeMcqId / activeSubjectiveId / blankSlideType
        {
            id: "mcq-display",
            section: "concept-check",
            mode: "mcq_display",
            title: "Concept Check",
            estimatedSeconds: 60,
            tags: ["assessment"],
            notes: ["Wait at least 30s before revealing."],
            misconceptions: [],
            fragments: [{ caption: "Read the question carefully." }],
        },
        {
            id: "subjective-display",
            section: "practice",
            mode: "subjective_display",
            title: "Practice / Solved Example",
            estimatedSeconds: 120,
            tags: ["subjective"],
            notes: ["Walk the room as students think."],
            misconceptions: [],
            fragments: [{ caption: "Take a moment to think this through." }],
        },
        {
            id: "drag-drop",
            section: "activity",
            mode: "drag_drop",
            title: "Drag & Drop — Inputs vs Outputs",
            estimatedSeconds: 180,
            tags: ["activity"],
            notes: ["Walk around — help groups that get stuck."],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "Drag each item into the correct box — Inputs or Outputs.",
                },
            ],
        },
        {
            id: "blank-slide",
            section: "tools",
            mode: "blank_slide",
            title: "Blank Teaching Slide",
            estimatedSeconds: 0,
            tags: ["whiteboard"],
            notes: ["Use the pen to draw / write — the AI can read it."],
            misconceptions: [],
            fragments: [{ caption: "" }],
        },
        {
            id: "real-life-example",
            section: "tools",
            mode: "real_life_example",
            title: "Real-life example",
            estimatedSeconds: 30,
            tags: ["analogy"],
            notes: ["Use vivid analogy to anchor the concept."],
            misconceptions: [],
            fragments: [{ caption: "" }],
        },

        // RECAP SECTION -------------------------------------------------------
        {
            id: "recap",
            section: "recap",
            mode: "recap",
            title: "Today's takeaways",
            estimatedSeconds: 90,
            tags: ["summary"],
            notes: ["End with the 'every food chain' line."],
            misconceptions: [],
            takeaways: [
                {
                    head: "Photosynthesis",
                    body: "Plants make food using sunlight, water and CO₂.",
                },
                {
                    head: "Chlorophyll",
                    body: "The green pigment that captures sunlight.",
                },
                {
                    head: "Outputs",
                    body: "Glucose feeds the plant. Oxygen feeds us.",
                },
                {
                    head: "Why it matters",
                    body: "Almost every food chain on Earth begins here.",
                },
            ],
            fragments: [
                { caption: "Let's quickly recap what we learned today." },
                { caption: "Plants make food using sunlight, water and CO₂." },
                { caption: "Chlorophyll inside chloroplasts captures sunlight." },
                {
                    caption: "Outputs: glucose for the plant, and oxygen for us.",
                },
                {
                    caption:
                        "Almost every food chain on Earth begins with this. Great work today!",
                },
            ],
        },
    ],

    /* ---- sections config ---- */
    sections: [
        {
            id: "concept",
            title: "Concept",
            description: "Auto-flows unless paused",
            slideIds: [
                "intro",
                "what-is",
                "ingredients",
                "sunlight",
                "watch-leaf",
                "chlorophyll",
                "equation",
            ],
        },
        { id: "concept-check", title: "Concept Check", description: "5 MCQs" },
        {
            id: "practice",
            title: "Practice",
            description: "6 subjective questions",
        },
        {
            id: "activity",
            title: "Activity",
            description: "Drag & Drop",
            slideIds: ["drag-drop"],
        },
        { id: "recap", title: "Recap", description: "Key takeaways", slideIds: ["recap"] },
    ],

    /* ---- 5 concept-check MCQs ---- */
    mcqs: [
        {
            id: "m1",
            question: "Which gas do plants take in during photosynthesis?",
            options: ["Oxygen", "Nitrogen", "Carbon dioxide", "Hydrogen"],
            correctIndex: 2,
            difficulty: "easy",
            tag: "inputs",
            estimatedSeconds: 30,
            relatedConceptId: "ingredients",
            explanation:
                "Plants absorb carbon dioxide from the air through tiny pores called stomata.",
        },
        {
            id: "m2",
            question: "Which gas is released during photosynthesis?",
            options: ["Carbon dioxide", "Hydrogen", "Nitrogen", "Oxygen"],
            correctIndex: 3,
            difficulty: "easy",
            tag: "outputs",
            estimatedSeconds: 30,
            relatedConceptId: "equation",
            explanation: "Plants give off oxygen as a by-product — the very air we breathe.",
        },
        {
            id: "m3",
            question: "Which part of the plant mainly performs photosynthesis?",
            options: ["Roots", "Stems", "Leaves", "Flowers"],
            correctIndex: 2,
            difficulty: "easy",
            tag: "structure",
            estimatedSeconds: 30,
            relatedConceptId: "chlorophyll",
            explanation: "Leaves contain most of the chloroplasts — the green factories.",
        },
        {
            id: "m4",
            question: "What is the role of chlorophyll?",
            options: [
                "Stores food",
                "Absorbs sunlight",
                "Holds water",
                "Releases oxygen",
            ],
            correctIndex: 1,
            difficulty: "medium",
            tag: "chlorophyll",
            estimatedSeconds: 45,
            relatedConceptId: "chlorophyll",
            explanation:
                "Chlorophyll absorbs sunlight energy that drives the whole reaction.",
        },
        {
            id: "m5",
            question: "Which of these is NOT needed for photosynthesis?",
            options: ["Sunlight", "Water", "Carbon dioxide", "Soil"],
            correctIndex: 3,
            difficulty: "medium",
            tag: "inputs",
            estimatedSeconds: 45,
            relatedConceptId: "ingredients",
            explanation:
                "Soil is not directly needed — water and minerals come from soil but soil itself is not an input.",
        },
    ],

    /* ---- 6 subjective questions ---- */
    subjectives: [
        {
            id: "s1",
            question: "Why is sunlight necessary for photosynthesis?",
            difficulty: "easy",
            tag: "energy",
            estimatedSeconds: 120,
            relatedConceptId: "sunlight",
            hint: "Think about what powers the chemical reaction.",
            expectedPoints: [
                "Sunlight is the energy source.",
                "Without it, the reaction cannot happen.",
                "Chlorophyll captures this sunlight.",
            ],
            commonMistake: "Saying 'sunlight is food' — sunlight is energy.",
            steps: [
                {
                    title: "Recall the inputs",
                    body: "Photosynthesis needs water, CO₂ and sunlight.",
                },
                {
                    title: "Identify what sunlight does",
                    body: "Sunlight provides the energy that drives the reaction.",
                },
                {
                    title: "Explain the consequence",
                    body: "Without sunlight, plants cannot convert CO₂ + water into glucose.",
                },
            ],
        },
        {
            id: "s2",
            question: "What is the role of chlorophyll in photosynthesis?",
            difficulty: "easy",
            tag: "chlorophyll",
            estimatedSeconds: 120,
            relatedConceptId: "chlorophyll",
            hint: "Why are leaves green?",
            expectedPoints: [
                "Chlorophyll is a green pigment.",
                "It absorbs sunlight.",
                "Without it, sunlight cannot be used.",
            ],
            commonMistake: "Confusing chlorophyll with chloroplast.",
            steps: [
                {
                    title: "Define chlorophyll",
                    body: "Chlorophyll is the green pigment inside chloroplasts.",
                },
                {
                    title: "Its job",
                    body: "It absorbs sunlight energy.",
                },
                {
                    title: "Why this matters",
                    body: "Without chlorophyll the plant cannot trap energy from light.",
                },
            ],
        },
        {
            id: "s3",
            question: "Why are leaves called the food factories of the plant?",
            difficulty: "medium",
            tag: "structure",
            estimatedSeconds: 120,
            relatedConceptId: "chlorophyll",
            hint: "Think about where photosynthesis takes place.",
            expectedPoints: [
                "Most photosynthesis happens in leaves.",
                "Leaves contain chlorophyll-rich chloroplasts.",
                "They produce glucose — the plant's food.",
            ],
            commonMistake: "Saying photosynthesis happens in roots.",
            steps: [
                { title: "Locate the action", body: "Photosynthesis mostly happens in leaves." },
                { title: "Why leaves?", body: "Leaves are flat and full of chloroplasts — perfect for catching sunlight." },
                { title: "Output", body: "They make glucose — that's why we call them food factories." },
            ],
        },
        {
            id: "s4",
            question: "What would happen if a plant is kept in darkness for many days?",
            difficulty: "medium",
            tag: "energy",
            estimatedSeconds: 120,
            relatedConceptId: "sunlight",
            hint: "What can't the plant do without sunlight?",
            expectedPoints: [
                "Photosynthesis stops without sunlight.",
                "Plant cannot make new food.",
                "Eventually the plant withers and may die.",
            ],
            commonMistake: "Saying the plant will grow faster in darkness.",
            steps: [
                { title: "What stops?", body: "Photosynthesis requires sunlight — it stops in darkness." },
                { title: "Consequence", body: "No new food (glucose) is produced." },
                { title: "Long term", body: "The plant uses up stored food, weakens and eventually dies." },
            ],
        },
        {
            id: "s5",
            question: "Explain the photosynthesis equation in your own words.",
            difficulty: "medium",
            tag: "equation",
            estimatedSeconds: 120,
            relatedConceptId: "equation",
            hint: "Translate inputs and outputs into everyday language.",
            expectedPoints: [
                "Inputs: water + carbon dioxide + sunlight.",
                "Outputs: glucose + oxygen.",
                "Sunlight is the energy that drives it.",
            ],
            commonMistake: "Including soil as an input.",
            steps: [
                { title: "Inputs", body: "Plants take in water from soil and CO₂ from air." },
                { title: "Energy", body: "Sunlight, captured by chlorophyll, drives the reaction." },
                { title: "Outputs", body: "Plants produce glucose (food) and release oxygen." },
            ],
        },
        {
            id: "s6",
            question: "How do plants help maintain oxygen in the air?",
            difficulty: "hard",
            tag: "outputs",
            estimatedSeconds: 150,
            relatedConceptId: "equation",
            hint: "Connect photosynthesis to the air we breathe.",
            expectedPoints: [
                "Plants release oxygen as a by-product.",
                "Animals (including us) consume oxygen.",
                "Plants replenish atmospheric oxygen.",
            ],
            commonMistake: "Thinking only forests do this — all plants contribute.",
            steps: [
                { title: "What plants release", body: "Plants release oxygen during photosynthesis." },
                { title: "What we do", body: "Animals consume oxygen for respiration." },
                { title: "The balance", body: "Plants continuously replenish oxygen — keeping the atmosphere breathable." },
            ],
        },
    ],

    /* ---- drag-and-drop activity ---- */
    activity: {
        id: "drag-drop-1",
        title: "Match Inputs and Outputs",
        objective: "Place each item into the correct category.",
        instruction: "Drag each label into Inputs or Outputs.",
        difficulty: "easy",
        duration: 180,
        items: [
            { id: "sun", label: "Sunlight", correct: "inputs" },
            { id: "co2", label: "Carbon dioxide", correct: "inputs" },
            { id: "h2o", label: "Water", correct: "inputs" },
            { id: "glu", label: "Glucose", correct: "outputs" },
            { id: "o2", label: "Oxygen", correct: "outputs" },
            // distractors
            { id: "soil", label: "Soil", correct: null },
            { id: "moon", label: "Moonlight", correct: null },
            { id: "n2", label: "Nitrogen", correct: null },
            { id: "smoke", label: "Smoke", correct: null },
        ],
        categories: [
            { id: "inputs", label: "Inputs" },
            { id: "outputs", label: "Outputs" },
        ],
    },

    /* ---- real-life examples ---- */
    realLifeExamples: [
        {
            id: "e1",
            text: "Leaves are like tiny food factories.",
            headline: "Tiny green factories",
            description:
                "Inside every leaf, millions of microscopic factories called chloroplasts work non-stop, packaging sunlight into food.",
            image:
                "https://images.unsplash.com/photo-1469289759076-d1484757abc3?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e2",
            text: "Sunlight is like the energy source in a kitchen.",
            headline: "Sunlight powers the kitchen",
            description:
                "Just like a stove powers cooking, sunlight powers the chemistry inside leaves — no sun, no food.",
            image:
                "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e3",
            text: "Chlorophyll is like the solar panel of the plant.",
            headline: "Nature's solar panel",
            description:
                "Chlorophyll absorbs sunlight just like a solar panel absorbs energy. That's why leaves are green.",
            image:
                "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e4",
            text: "Plants take in carbon dioxide like we breathe in oxygen.",
            headline: "Plants 'breathe' too",
            description:
                "We exhale CO₂ — plants take it in through tiny pores called stomata. A perfect partnership.",
            image:
                "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "e5",
            text: "A plant in a dark cupboard slowly starves — like skipping every meal.",
            headline: "No light, no food",
            description:
                "Without sunlight, photosynthesis halts. The plant uses up reserves and gradually weakens — exactly like skipping every meal.",
            image:
                "https://images.unsplash.com/photo-1520412099551-62b6bafeb5bb?auto=format&fit=crop&w=1600&q=80",
        },
    ],

    /* ---- teacher-help library, keyed by concept ---- */
    teacherHelp: {
        what: {
            simple:
                "Photosynthesis is how plants make their own food using sunlight, water and air.",
            probing: "Why do you think plants are green?",
            misconception: "Students may think plants eat soil. Sunlight + air + water are the real ingredients.",
            analogy: "Think of leaves as tiny solar-powered kitchens.",
        },
        ingredients: {
            simple:
                "Plants need three things: water from the soil, carbon dioxide from the air, and sunlight as energy.",
            probing: "Where does each ingredient come from?",
            misconception: "Soil is not an ingredient — it provides water and minerals, but isn't directly used in photosynthesis.",
            analogy: "Like baking a cake — water, gas (CO₂), and heat (sunlight).",
        },
        sunlight: {
            simple: "Sunlight is the energy that powers photosynthesis. Without it, the reaction can't happen.",
            probing: "What happens to a plant kept in a dark cupboard?",
            misconception: "Sunlight is energy — not food.",
            analogy: "Like a phone battery — no charging, no work.",
        },
        chlorophyll: {
            simple:
                "Chlorophyll is the green pigment in leaves that captures sunlight.",
            probing: "Why do you think most leaves are green?",
            misconception:
                "Students often confuse chlorophyll (the pigment) with chloroplast (the structure that contains it).",
            analogy: "Chlorophyll is like a solar panel — chloroplast is the whole solar farm.",
        },
        equation: {
            simple:
                "6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂. Plants combine carbon dioxide and water using sunlight to make glucose and release oxygen.",
            probing: "Which side of the equation has oxygen, and why?",
            misconception: "Students may try to balance it like simple addition — emphasize molecules combine and rearrange.",
            analogy: "Like a recipe — fixed ingredients always give the same dish.",
        },
    },

    /* ---- AI recommendations, keyed by slide id ---- */
    aiRecommendations: {
        intro: [
            {
                id: "rec-intro-1",
                text: "Ask students to predict before revealing — builds curiosity.",
                action: "annotate",
            },
        ],
        "what-is": [
            {
                id: "rec-what-1",
                text: "Good moment to ask Concept Check Q1: 'Which gas do plants take in?'",
                action: "show-mcq",
                target: "m1",
            },
            {
                id: "rec-what-2",
                text: "Highlight the word 'own' — plants make their OWN food.",
                action: "annotate",
            },
        ],
        ingredients: [
            {
                id: "rec-ing-1",
                text: "Run the drag-and-drop activity to reinforce inputs.",
                action: "start-activity",
            },
            {
                id: "rec-ing-2",
                text: "Suggested next: ask MCQ Q5 — 'Which is NOT needed?'",
                action: "show-mcq",
                target: "m5",
            },
            {
                id: "rec-ing-3",
                text: "Real-life example: 'Plants take in CO₂ like we breathe in O₂.'",
                action: "show-example",
                target: "e4",
            },
        ],
        sunlight: [
            {
                id: "rec-sun-1",
                text: "Common misconception: students may say 'plants eat sunlight'. Address it.",
                action: "show-help",
                target: "sunlight",
            },
            {
                id: "rec-sun-2",
                text: "Use S4 as a solved example: 'What happens in darkness?'",
                action: "use-solved",
                target: "s4",
            },
        ],
        "watch-leaf": [
            {
                id: "rec-vid-1",
                text: "Pause the video at the green parts — ask 'why is the leaf green?'",
                action: "pause",
            },
            {
                id: "rec-vid-2",
                text: "After playback, jump to the chloroplast diagram for a smooth transition.",
                action: "show-help",
                target: "chlorophyll",
            },
        ],
        chlorophyll: [
            {
                id: "rec-chl-1",
                text: "Use the pen to circle the chloroplast — visual emphasis helps recall.",
                action: "annotate",
            },
            {
                id: "rec-chl-2",
                text: "Ask MCQ Q4 to check the chlorophyll concept now.",
                action: "show-mcq",
                target: "m4",
            },
            {
                id: "rec-chl-3",
                text: "Need a deeper explanation? Ask AI Help on chlorophyll.",
                action: "show-help",
                target: "chlorophyll",
            },
        ],
        equation: [
            {
                id: "rec-eq-1",
                text: "Now's the time for MCQ Q2: 'Which gas is released?'",
                action: "show-mcq",
                target: "m2",
            },
            {
                id: "rec-eq-2",
                text: "Assign S5 as practice — 'Explain the equation in your own words'.",
                action: "assign-practice",
                target: "s5",
            },
            {
                id: "rec-eq-3",
                text: "Pause briefly — let students copy the equation in their notebooks.",
                action: "pause",
            },
        ],
        "mcq-display": [
            {
                id: "rec-mcq-1",
                text: "Wait at least 30 seconds before revealing the answer.",
                action: "pause",
            },
        ],
        "subjective-display": [
            {
                id: "rec-sub-1",
                text: "Walk the room — glance at notebooks before revealing.",
                action: "pause",
            },
        ],
        "drag-drop": [
            {
                id: "rec-dd-1",
                text: "After 1 minute, reveal correct placements.",
                action: "show-activity-answer",
            },
        ],
        recap: [
            {
                id: "rec-rcp-1",
                text: "End with 'every food chain on Earth begins here' — sticks with students.",
                action: "annotate",
            },
        ],
    },

    /* ---- blank slide types ---- */
    blankSlideTypes: [
        { id: "white", label: "White board" },
        { id: "lined", label: "Lined" },
        { id: "grid", label: "Grid" },
        { id: "diagram", label: "Diagram" },
    ],
};

/* ---- helpers ---- */

// Active-lesson registry. We keep this inside lessonData.js to avoid a
// circular import between lessonData ↔ lessonRegistry (webpack TDZ).
// quadraticLessonData has no dependency on this file, so the one-way
// import below is safe.
import { quadraticLesson } from "./quadraticLessonData";
import { soundWavesLesson } from "./soundWavesLessonData";
import { angleLesson } from "./angleLessonData";

const LESSONS = {
    "angle-types-and-basics": angleLesson,
    photosynthesis: photosynthesisLesson,
    "quadratic-equations": quadraticLesson,
    "characteristics-of-sound-waves": soundWavesLesson,
};

let _activeLessonId = "photosynthesis";

export const setActiveLessonId = (id) => {
    if (id && LESSONS[id]) _activeLessonId = id;
};
export const getActiveLessonId = () => _activeLessonId;
export const getCurrentLesson = () =>
    LESSONS[_activeLessonId] || photosynthesisLesson;
export const listLessons = () =>
    Object.entries(LESSONS).map(([id, l]) => ({
        id,
        title: l.sessionName || l.title,
        subject: l.subject || "General",
        grade: l.grade,
        chapter: l.chapter,
        subtitle: l.subtitle,
        slidesCount: l.slides?.length || 0,
        sectionsCount: l.sections?.length || 0,
    }));

// Active lesson Proxy. Each `lesson.foo` access resolves through the
// registry, so the same `lesson` reference can swap to a different
// lesson (e.g. quadratic-equations) at runtime by calling
// setActiveLessonId(...).
export const lesson = new Proxy(
    {},
    {
        get(_t, prop) {
            return getCurrentLesson()[prop];
        },
        has(_t, prop) {
            return prop in getCurrentLesson();
        },
        ownKeys() {
            return Reflect.ownKeys(getCurrentLesson());
        },
        getOwnPropertyDescriptor(_t, prop) {
            const d = Object.getOwnPropertyDescriptor(
                getCurrentLesson(),
                prop,
            );
            if (d) d.configurable = true;
            return d;
        },
    },
);

export const MASCOT_STATES = [
    "idle",
    "speaking",
    "listening",
    "thinking",
    "explaining",
    "video",
];

export const defaultMascotForMode = (mode) => {
    switch (mode) {
        case "intro":
            return "speaking";
        case "concept_teaching":
        case "ai_explanation":
            return "explaining";
        case "diagram_teaching":
            return "explaining";
        case "video_teaching":
            return "video";
        case "mcq_display":
        case "question_display":
            return "thinking";
        case "subjective_display":
            return "thinking";
        case "think_pause":
            return "listening";
        case "practice_activity":
        case "drag_drop":
            return "listening";
        case "solution_reveal":
            return "explaining";
        case "blank_slide":
            return "explaining";
        case "real_life_example":
            return "speaking";
        case "recap":
            return "speaking";
        default:
            return "idle";
    }
};

export const slideTypeLabel = (mode) => {
    const m = {
        intro: "Intro",
        concept_teaching: "Concept",
        diagram_teaching: "Diagram",
        video_teaching: "Video",
        mcq_display: "MCQ",
        subjective_display: "Subjective",
        drag_drop: "Drag & Drop",
        blank_slide: "Blank",
        practice_activity: "Activity",
        solution_reveal: "Solution",
        recap: "Recap",
        real_life_example: "Real life",
        think_pause: "Think",
    };
    return m[mode] || mode;
};

export const findSlideIdxById = (slides, id) =>
    slides.findIndex((s) => s.id === id);

export const findMcqById = (id) => lesson.mcqs.find((m) => m.id === id);
export const findSubjectiveById = (id) =>
    lesson.subjectives.find((s) => s.id === id);
export const findExampleById = (id) =>
    lesson.realLifeExamples.find((e) => e.id === id);

/* mocked AI handwriting detection — used by blank-slide UI flow */
export const mockHandwritingDetect = () => ({
    detected: "CO2 + water + sunlight = food + oxygen",
    suggestion:
        "Would you like to explain this as: Plants use carbon dioxide from air and water from soil, with sunlight captured by chlorophyll, to make glucose and release oxygen?",
    cleanedVersion: "6CO₂ + 6H₂O + sunlight → C₆H₁₂O₆ + 6O₂",
});
